import * as React from "react";
import { useState } from "react";
import { format } from "date-fns";
import { CalendarDays, Clock, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Button, buttonVariants } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";

// ─── Canonical ContentOS date/time primitives ──────────────────────────────
// DatePicker      → date-only  (value "YYYY-MM-DD")  — quick picks + calendar
// TimePicker      → time-only  (value "HH:mm")       — direct time selection
// DateTimePicker  → date+time  (value "YYYY-MM-DDTHH:mm", naive local) — Date/Time
//                   segmented interaction (Reminder).
// Desktop/tablet: collision-aware Popover. Mobile: viewport-safe bottom Sheet
// with sticky header / scrollable body / sticky footer (Cancel discards the
// picker session, Set/Apply commits into the Task form — the Task Drawer's own
// Save/Create stays authoritative). Data contracts are unchanged.

const parseDate = (v) => { if (!v) return null; const d = new Date(`${v}T00:00:00`); return isNaN(d.getTime()) ? null : d; };
const parseDateTime = (v) => { if (!v) return null; const d = new Date(v); return isNaN(d.getTime()) ? null : d; };
const todayStart = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const ymd = (d) => format(d, "yyyy-MM-dd");

const friendlyDate = (d) => {
  if (!d) return "";
  const base = new Date(d); base.setHours(0, 0, 0, 0);
  const diff = Math.round((base - todayStart()) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  return format(d, "d MMM yyyy");
};
const friendlyTime = (hhmm) => {
  if (!hhmm) return "";
  const d = new Date(`2000-01-01T${hhmm}`);
  return isNaN(d.getTime()) ? hhmm : format(d, "h:mm a");
};

// Future-focused shortcuts (no "Yesterday" — see spec §7).
const QUICK = [
  { label: "Today", days: 0 },
  { label: "Tomorrow", days: 1 },
  { label: "Day after", days: 2 },
  { label: "Next week", days: 7 },
];

// Compact, properly-aligned calendar: both the weekday header row and each date
// row use a true 7-column grid so columns never drift; tight row height keeps it
// short enough to fit a mobile sheet without scroll-fighting.
const CAL_CLASSNAMES = {
  months: "flex flex-col",
  month: "space-y-2",
  caption: "flex justify-center pt-1 relative items-center",
  caption_label: "text-sm font-semibold",
  nav: "flex items-center gap-1",
  table: "w-full border-collapse",
  head_row: "grid grid-cols-7",
  head_cell: "flex h-7 items-center justify-center text-[0.72rem] font-normal text-muted-foreground",
  row: "grid grid-cols-7",
  cell: "relative flex h-10 items-center justify-center p-0 text-sm",
  day: cn(buttonVariants({ variant: "ghost" }), "h-9 w-9 rounded-full p-0 font-normal aria-selected:opacity-100"),
  day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
  day_today: "bg-accent/50 font-semibold text-accent-foreground",
  day_outside: "text-muted-foreground/50",
  day_disabled: "text-muted-foreground/30 opacity-50",
};

function CompactCalendar({ selected, onSelect, disablePast }) {
  return (
    <Calendar mode="single" selected={selected || undefined} onSelect={onSelect}
      disabled={disablePast ? { before: todayStart() } : undefined}
      showOutsideDays className="p-0" classNames={CAL_CLASSNAMES} />
  );
}

function QuickPicks({ onPick, testid }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1" data-testid={`${testid}-quickpicks`}>
      {QUICK.map((q) => (
        <button key={q.label} type="button" onClick={() => onPick(ymd(addDays(todayStart(), q.days)))}
          data-testid={`${testid}-qp-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
          className="shrink-0 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground transition-colors hover:border-accent hover:bg-accent-subtle">
          {q.label}
        </button>
      ))}
    </div>
  );
}

function Segmented({ tab, setTab, testid }) {
  return (
    <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1" data-testid={`${testid}-tabs`}>
      {["date", "time"].map((t) => (
        <button key={t} type="button" onClick={() => setTab(t)} data-testid={`${testid}-tab-${t}`}
          className={cn("rounded-md py-1.5 text-sm font-medium capitalize transition-colors", tab === t ? "bg-background text-foreground shadow-sm" : "text-muted-foreground")}>
          {t}
        </button>
      ))}
    </div>
  );
}

function BigTimeInput({ value, onChange, inputTestid }) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Time</label>
      <Input type="time" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={inputTestid} className="h-11 text-base" />
    </div>
  );
}

// Viewport-safe mobile bottom sheet: sticky header + scrollable body + sticky
// footer, sized to the visible viewport (dvh) and safe-area aware so the footer
// is always reachable even with mobile browser chrome present.
function MobileSheet({ open, onOpenChange, title, summary, testid, children, footer }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="flex max-h-[90dvh] flex-col gap-0 rounded-t-2xl p-0" data-testid={`${testid}-sheet`}>
        <div className="shrink-0 border-b border-border px-4 pb-3 pt-4">
          <SheetTitle className="text-base">{title}</SheetTitle>
          {summary != null && <p className="mt-0.5 text-sm font-medium text-accent" data-testid={`${testid}-summary`}>{summary}</p>}
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3" data-testid={`${testid}-panel`}>{children}</div>
        {footer}
      </SheetContent>
    </Sheet>
  );
}

function SheetFooterBar({ onClear, onCancel, onApply, applyLabel, canClear, canApply, testid }) {
  return (
    <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border bg-card px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
      <Button type="button" variant="ghost" size="sm" onClick={onClear} disabled={!canClear} data-testid={`${testid}-clear-btn`}>Clear</Button>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} data-testid={`${testid}-cancel`}>Cancel</Button>
        <Button type="button" size="sm" onClick={onApply} disabled={!canApply} data-testid={`${testid}-apply`}>{applyLabel}</Button>
      </div>
    </div>
  );
}

// Whole-field trigger — forwardRef so Radix PopoverTrigger `asChild` can inject
// onClick/ref, while we still spread an explicit onClick for the mobile Sheet.
const TriggerField = React.forwardRef(({ icon: Icon, active, text, placeholder, onClear, testid, ...props }, ref) => (
  <button ref={ref} type="button" data-testid={testid} {...props}
    className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-left text-sm shadow-sm transition-colors hover:border-accent focus:outline-none focus:ring-1 focus:ring-ring">
    <Icon className={cn("h-4 w-4 shrink-0", active ? "text-accent" : "text-muted-foreground")} />
    <span className={cn("flex-1 truncate", !active && "text-muted-foreground")}>{active ? text : placeholder}</span>
    {active && onClear && (
      <span role="button" tabIndex={0} onClick={onClear} onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClear(e); }}
        data-testid={`${testid}-clear`} aria-label="Clear value"
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground/60 hover:bg-muted hover:text-destructive">
        <X className="h-3.5 w-3.5" />
      </span>
    )}
  </button>
));
TriggerField.displayName = "TriggerField";

export function DatePicker({ value, onChange, testid = "date-picker", placeholder = "Set date", clearable = true, side, align = "start", sideOffset = 6, alignOffset = 0, disablePast = false }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const selected = parseDate(value);
  const clearImmediate = (e) => { e?.stopPropagation?.(); e?.preventDefault?.(); onChange(""); setOpen(false); };
  const trigger = (onClick) => (
    <TriggerField icon={CalendarDays} active={!!value} text={friendlyDate(selected)} placeholder={placeholder}
      onClear={clearable ? clearImmediate : undefined} onClick={onClick} testid={testid} />
  );

  if (isMobile) {
    const openSheet = () => { setDraft(value || ""); setOpen(true); };
    const draftDate = parseDate(draft);
    return (
      <>
        {trigger(openSheet)}
        <MobileSheet open={open} onOpenChange={setOpen} testid={testid} title="Select date"
          summary={draftDate ? friendlyDate(draftDate) : "No date selected"}
          footer={<SheetFooterBar testid={testid} canClear={!!value} onClear={() => { onChange(""); setOpen(false); }}
            onCancel={() => setOpen(false)} onApply={() => { onChange(draft); setOpen(false); }} applyLabel="Set date" canApply={!!draft} />}>
          <div className="space-y-3">
            <QuickPicks testid={testid} onPick={(d) => setDraft(d)} />
            <CompactCalendar selected={draftDate} disablePast={disablePast} onSelect={(d) => setDraft(d ? ymd(d) : "")} />
          </div>
        </MobileSheet>
      </>
    );
  }

  const pick = (d) => { if (d) { onChange(ymd(d)); setOpen(false); } };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger(undefined)}</PopoverTrigger>
      <PopoverContent side={side} align={align} sideOffset={sideOffset} alignOffset={alignOffset} avoidCollisions collisionPadding={16} className="flex max-h-[var(--radix-popover-content-available-height)] w-[320px] flex-col gap-0 overflow-hidden p-0" data-testid={`${testid}-popover`}>
        <div className="flex-1 space-y-3 overflow-y-auto p-3" data-testid={`${testid}-panel`}>
          <QuickPicks testid={testid} onPick={(d) => { onChange(d); setOpen(false); }} />
          <CompactCalendar selected={selected} disablePast={disablePast} onSelect={pick} />
        </div>
        {clearable && <div className="flex shrink-0 justify-end border-t border-border p-2"><Button type="button" variant="ghost" size="sm" onClick={clearImmediate} disabled={!value} data-testid={`${testid}-clear-btn`}>Clear</Button></div>}
      </PopoverContent>
    </Popover>
  );
}

export function TimePicker({ value, onChange, testid = "time-picker", placeholder = "Set time", clearable = true, side, align = "start", sideOffset = 6, alignOffset = 0 }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const clearImmediate = (e) => { e?.stopPropagation?.(); e?.preventDefault?.(); onChange(""); setOpen(false); };
  const trigger = (onClick) => (
    <TriggerField icon={Clock} active={!!value} text={friendlyTime(value)} placeholder={placeholder}
      onClear={clearable ? clearImmediate : undefined} onClick={onClick} testid={testid} />
  );

  if (isMobile) {
    const openSheet = () => { setDraft(value || ""); setOpen(true); };
    return (
      <>
        {trigger(openSheet)}
        <MobileSheet open={open} onOpenChange={setOpen} testid={testid} title="Select time"
          summary={draft ? friendlyTime(draft) : "No time selected"}
          footer={<SheetFooterBar testid={testid} canClear={!!value} onClear={() => { onChange(""); setOpen(false); }}
            onCancel={() => setOpen(false)} onApply={() => { onChange(draft); setOpen(false); }} applyLabel="Set time" canApply={!!draft} />}>
          <BigTimeInput value={draft} onChange={setDraft} inputTestid={`${testid}-input`} />
        </MobileSheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger(undefined)}</PopoverTrigger>
      <PopoverContent side={side} align={align} sideOffset={sideOffset} alignOffset={alignOffset} avoidCollisions collisionPadding={16} className="w-56 p-3" data-testid={`${testid}-popover`}>
        <div className="space-y-3" data-testid={`${testid}-panel`}>
          <BigTimeInput value={value || ""} onChange={onChange} inputTestid={`${testid}-input`} />
          <div className="flex items-center justify-between border-t border-border pt-2">
            {clearable ? <Button type="button" variant="ghost" size="sm" onClick={clearImmediate} disabled={!value} data-testid={`${testid}-clear-btn`}>Clear</Button> : <span />}
            <Button type="button" size="sm" onClick={() => setOpen(false)} data-testid={`${testid}-done`}>Done</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function DateTimePicker({ value, onChange, testid = "datetime-picker", placeholder = "Set date & time", title = "Select date & time", icon = CalendarDays, disablePast = false }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [dDate, setDDate] = useState("");
  const [dTime, setDTime] = useState("09:00");
  const [tab, setTab] = useState("date");
  const seed = () => {
    setDDate(value ? value.slice(0, 10) : "");
    setDTime(value ? value.slice(11, 16) : "09:00");
    setTab("date");
  };
  const clearImmediate = (e) => { e?.stopPropagation?.(); e?.preventDefault?.(); onChange(""); setOpen(false); };
  const apply = () => { if (!dDate) return; onChange(`${dDate}T${dTime || "09:00"}`); setOpen(false); };
  const label = value ? `${friendlyDate(parseDateTime(value))} · ${friendlyTime(value.slice(11, 16))}` : "";
  const draftSummary = dDate ? `${friendlyDate(parseDate(dDate))} · ${friendlyTime(dTime)}` : "Pick a date";
  const trigger = (onClick) => (
    <TriggerField icon={icon} active={!!value} text={label} placeholder={placeholder} onClear={clearImmediate} onClick={onClick} testid={testid} />
  );

  if (isMobile) {
    const openSheet = () => { seed(); setOpen(true); };
    return (
      <>
        {trigger(openSheet)}
        <MobileSheet open={open} onOpenChange={setOpen} testid={testid} title={title} summary={draftSummary}
          footer={<SheetFooterBar testid={testid} canClear={!!value} onClear={() => { onChange(""); setOpen(false); }}
            onCancel={() => setOpen(false)} onApply={apply} applyLabel="Set reminder" canApply={!!dDate} />}>
          <div className="space-y-3">
            <Segmented tab={tab} setTab={setTab} testid={testid} />
            {tab === "date" ? (
              <div className="space-y-3">
                <QuickPicks testid={testid} onPick={(d) => { setDDate(d); setTab("time"); }} />
                <CompactCalendar selected={parseDate(dDate)} disablePast={disablePast} onSelect={(d) => { if (d) { setDDate(ymd(d)); setTab("time"); } }} />
              </div>
            ) : (
              <BigTimeInput value={dTime} onChange={setDTime} inputTestid={`${testid}-time`} />
            )}
          </div>
        </MobileSheet>
      </>
    );
  }

  const handleOpen = (next) => { if (next) seed(); setOpen(next); };
  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>{trigger(undefined)}</PopoverTrigger>
      <PopoverContent align="start" avoidCollisions collisionPadding={16} className="flex max-h-[var(--radix-popover-content-available-height)] w-[320px] flex-col gap-0 overflow-hidden p-0" data-testid={`${testid}-popover`}>
        <div className="flex-1 space-y-3 overflow-y-auto p-3" data-testid={`${testid}-panel`}>
          <QuickPicks testid={testid} onPick={(d) => setDDate(d)} />
          <CompactCalendar selected={parseDate(dDate)} disablePast={disablePast} onSelect={(d) => setDDate(d ? ymd(d) : "")} />
          <div className="border-t border-border pt-3"><BigTimeInput value={dTime} onChange={setDTime} inputTestid={`${testid}-time`} /></div>
        </div>
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border p-3">
          <Button type="button" variant="ghost" size="sm" onClick={clearImmediate} disabled={!value && !dDate} data-testid={`${testid}-clear-btn`}>Clear</Button>
          <Button type="button" size="sm" onClick={apply} disabled={!dDate} data-testid={`${testid}-done`}>Done</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
